import { Injectable } from "@angular/core";
import { Actions, ofType, createEffect } from "@ngrx/effects";
import { switchMap, catchError, tap, map, filter } from "rxjs/operators";
import { Observable, of, Subject, throwError } from "rxjs";
import {
    ActionTypes
} from "./report.actions";
import { DownloadReportAction } from "./report.actions";
import { DownloadReportModalComponent } from "@admin_app/shared/download-report-modal/download-report-modal.component";
import { MatDialog } from "@angular/material/dialog";
import { ModalActionType } from "@admin_app/shared/modal-action";
import { GlobalSpinnerService, LocalTimePoint, VituHttpErrorNotFound } from "shared-lib";
import { BillingService } from "@admin_api/services/billing.service";
import { StrictHttpResponse } from "@admin_api/strict-http-response";
import { DownloadReportModalAction } from "@admin_app/shared/download-report-modal/download-report-modal-action";
import { ReportType } from "@admin_app/shared/download-report-modal/report-type";

@Injectable()
export class ReportEffects {

    constructor(
        private actions: Actions,
        private dialog: MatDialog,
        private billingService: BillingService,
        private globalSpinner: GlobalSpinnerService
    ) { }

    downloadReport = createEffect(() => this.actions.pipe(
            ofType<DownloadReportAction>(ActionTypes.DownloadReport),
            switchMap(downloadReportAction => {
                const errorMessageSubject = new Subject<string>();
                const modalActionSubject = new Subject<DownloadReportModalAction>();

                const dialogRef = this.dialog
                    .open(DownloadReportModalComponent, {
                        width: "400px",
                        data: {
                            reportType: downloadReportAction.reportType,
                            modalActionSubject,
                            errorMessageSubject
                        },
                        disableClose: true,
                        autoFocus: false
                    });

                return modalActionSubject.pipe(
                    switchMap(({action, params}: DownloadReportModalAction) => {
                        switch (action) {
                            case ModalActionType.CANCEL:
                            {
                                return of(true);
                            }
                            case ModalActionType.CONFIRM:
                            {
                                let reportRequest: Observable<any>;

                                if (params.requiresMids) {
                                    reportRequest = throwError(new Error("Need to include a merchant which contains at least one MID."));
                                }
                                else {
                                    reportRequest = throwError(new Error("Bad Report Type."));
                                    switch (downloadReportAction.reportType) {
                                        case ReportType.TRANSACTIONS:
                                            {
                                                reportRequest = this.billingService.reportTransactionReport$Response(
                                                    {
                                                        MerchantIds: params.merchantId,
                                                        MerchantProcessorIds: params.merchantProcessorIds,
                                                        StartDate: LocalTimePoint.convertLocalValueToUtcValue(params.startDate),
                                                        EndDate: LocalTimePoint.convertLocalValueToUtcValue(params.endDate)
                                                    });
                                                break;
                                            }
                                        case ReportType.STATEMENT:
                                            {
                                                reportRequest = this.billingService.reportStatementReports$Response(
                                                    {
                                                        merchantId: params.merchantId,
                                                        id: params.statementId
                                                    });
                                                break;
                                            }

                                        case ReportType.RECONCILIATION:
                                            {
                                                reportRequest = this.billingService.reportFundingReconciliation$Response(
                                                    {
                                                        merchantId: params.merchantId,
                                                        MerchantProcessorId: params.merchantProcessorIds,
                                                        CreatedDate: LocalTimePoint.convertLocalValueToUtcValue(params.startDate)
                                                    });
                                                break;
                                            }
                                        case ReportType.FUNDING:
                                            {
                                                reportRequest = this.billingService.reportFundingReport$Response(
                                                    {
                                                        MerchantIds: params.merchantId,
                                                        MerchantProcessorIds: params.merchantProcessorIds,
                                                        StartDate: LocalTimePoint.convertLocalValueToUtcValue(params.startDate),
                                                        EndDate: LocalTimePoint.convertLocalValueToUtcValue(params.endDate)
                                                    });
                                                break;
                                            }
                                    }
                                }
                                return this.globalSpinner.apply(reportRequest.pipe(
                                    map((response: any) => {
                                        const fileName = this.getDownloadFileName(response);
                                        this.openFile(fileName, response);
                                        return true;
                                    }),
                                    catchError(error => {
                                        // This will catch errors from both request failure & file opening failure
                                        let errorMessage = (error instanceof Error) ? error.message : error;
                                        // Override specific error messages depending on reportType
                                        switch (downloadReportAction.reportType) {
                                            case ReportType.RECONCILIATION:
                                                {
                                                    if (error instanceof VituHttpErrorNotFound) {
                                                        errorMessage = "There were no batches processed on this date.";
                                                    }
                                                    break;
                                                }
                                        }
                                        errorMessageSubject.next(errorMessage);
                                        return of(false);
                                    })
                                ));

                            }
                        }
                    }),
                    filter((doClose: boolean) => doClose),
                    tap(() => dialogRef.close())
                );
            })
        ), { dispatch: false });

    private getDownloadFileName(response: StrictHttpResponse<Blob>): string {
        let fileName = null;

        try {
            const contentDisposition = response.headers.get("content-disposition");
            const regEx = /filename=[\"]?([^\"]+?)[\"]?;/;
            const fileNameMatches = contentDisposition.match(regEx);
            if (Array.isArray(fileNameMatches) && (fileNameMatches.length === 2)) {
                fileName = fileNameMatches[1];
            }
            if (!fileName) {
                throw new Error();
            }
        }
        catch (error) {
            throw new Error("Unable to download file.");
        }

        return fileName;
    }

    private openFile(fileName: string, response: StrictHttpResponse<Blob>): void {

        try {
            const blob = new Blob([response.body], { type: "application/octet-stream" });
            const fileUrl = URL.createObjectURL(blob);
            const tempAnchorElement: HTMLAnchorElement = document.createElement("a") as HTMLAnchorElement;
            tempAnchorElement.href = fileUrl;
            tempAnchorElement.download = fileName;
            document.body.appendChild(tempAnchorElement);
            tempAnchorElement.click();
            document.body.removeChild(tempAnchorElement);
            URL.revokeObjectURL(fileUrl);
        }
        catch (error) {
            throw new Error("Unable to open file.");
        }

    }

}
